import React from 'react';
import { VerifiedBadgeIcon, TickIcon } from '../custom-widgets/IconAssets';
import styles from './ManagerHero.module.css';
import SportImage from '../custom-widgets/SportImage';

export default function ManagerHero({ manager }) {
    const { display_name = '', name = '', portraitUrl = '', logoUrl = '', overview = [], clubName = '', clubLogoUrl = '' } = manager;
    
    const rawName = display_name || name || '';
    const bgText = rawName.toUpperCase();
    // Proportional font size — longer names automatically get smaller text
    const charCount = bgText.length; // includes spaces
    const bgNameSize = `${Math.max(6, Math.min(11, Math.floor(200 / charCount)))}vw`;
    const bgNameSizeMobile = `${Math.max(8, Math.min(22, Math.floor(240 / charCount)))}vw`;
    const bgLeft = charCount > 18 ? '55%' : charCount > 14 ? '60%' : charCount > 10 ? '65%' : '75%';

    // Extract metadata from overview array
    const clubItem  = overview.find(i => i.label.toLowerCase() === 'current club' || i.label.toLowerCase() === 'club');
    const ageItem   = overview.find(i => i.label.toLowerCase() === 'age');
    const natItem   = overview.find(i => i.label.toLowerCase() === 'nationality');
    const appItem   = overview.find(i => i.label.toLowerCase() === 'appointed at' || i.label.toLowerCase() === 'since');

    const clubNameValue  = clubItem?.value || clubName || '';
    const clubLogoValue  = clubLogoUrl || '';
    const age       = ageItem?.value   || '';
    const nat       = natItem?.value   || '';
    const appointed = appItem?.value   || '';

    const metaItems = [
        clubNameValue   ? { label: 'Club',         value: clubNameValue,  logo: clubLogoValue } : null,
        age        ? { label: 'Age',           value: age } : null,
        nat        ? { label: 'Nationality',   value: nat } : null,
        appointed  ? { label: 'Appointed At',  value: appointed } : null,
    ].filter(Boolean);

    return (
        <header className={styles.hero}>
            <div className={styles.heroBgWrapper}>
                {/* Particles overlay — right-side golden sparkle */}
                <div className={styles.heroParticles} />

                {/* Gold bar with frame texture for mobile glitter effect */}
                <div className={styles.heroGoldBar} />

                {/* Large background name text — positioned on the left */}
                {bgText && (
                    <div
                        className={styles.backgroundName}
                        style={{
                            '--bg-name-size': bgNameSize,
                            '--bg-name-size-mobile': bgNameSizeMobile,
                            '--bg-name-left': bgLeft
                        }}
                    >
                        {bgText}
                    </div>
                )}

                {/* Manager portrait — on the right */}
                <div className={styles.portraitContainer}>
                    <SportImage 
                        src={portraitUrl || logoUrl} 
                        fallbackSrc={"/player/fallback.png"}
                        // fallbackSrc="http://localhost:3000/player/fallback.png"
                        alt={name} 
                        className={styles.portrait} 
                    />
                </div>
            </div>

            {/* Identity content — on the left, absolutely positioned at bottom */}
            <div className={styles.heroContent}>
                <div className={styles['forensic-status-badge']}>
                    <VerifiedBadgeIcon width={22} height={22} />
                    <span>Forensic Verified</span>
                </div>

                <div className={styles.nameRow}>
                    <h1 className={styles.managerName}>{name}</h1>
                    <span className={styles.checkmark}>
                        <TickIcon />
                    </span>
                </div>

                {/* Metadata boxes strip */}
                {metaItems.length > 0 && (
                    <div className={styles.metaStrip}>
                        {metaItems.map((item, i) => (
                            <div key={i} className={styles.metaBox}>
                                {item.logo && (
                                    <img src={item.logo} alt={item.value} className={styles.metaClubLogo} />
                                )}
                                <div className={styles.metaText}>
                                    <span className={styles.metaLabel}>{item.label}</span>
                                    <span className={styles.metaValue}>{item.value}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </header>
    );
}
